import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '@/stores/AuthStore.js'

// Static component imports
import SoonPage from './default/SoonPage.vue';
import AccountLogin from './account/AccountLogin.vue';
import AccountLoginProvider from './account/AccountLoginProvider.vue';
import AccountLoginOTP from './account/AccountLoginOTP.vue';
import AccountEnableMFA from './account/AccountEnableMFA.vue';
import AccountLoginExternal from './account/AccountLoginExternal.vue';
import PrivacyPolicy from './account/PrivacyPolicy.vue';
import ForgotPassword from './account/ForgotPassword.vue';
import ResetPassword from './account/ResetPassword.vue';
import ResetTemporaryPassword from './account/ResetTemporaryPassword.vue';
import AccountVerify from './account/AccountVerify.vue';
import CompletePassword from './account/CompletePassword.vue';
import BandwidthExceeded from './account/BandwidthExceeded.vue';
import TenantExpired from './account/TenantExpired.vue';
import SelectSubscription from './account/SelectSubscription.vue';
import AccountLogout from './account/AccountLogout.vue';
import ForcedLogout from './account/ForcedLogout.vue';
import AccountSignedUp from './account/AccountSignedUp.vue';
import AccountSalesman from './account/AccountSalesman.vue';
import ValidateTenantEmail from './tenant/ValidateTenantEmail.vue';
import Index from './default/Index.vue';
import NodesIndex from './nodes2/NodesIndex.vue';
import PodIndex from './pods2/PodIndex.vue';
import SearchIndex from './search/SearchIndex.vue';
import RecycleBinIndex from './recycle-bin/RecycleBinIndex.vue';
import AccountIndex from './console/account/AccountIndex.vue';
import ProfileMFA from './console/account/ProfileMFA.vue';
import ProfileNotifications from './console/account/ProfileNotifications.vue';
import UsersIndex from './console/users/UsersIndex.vue';
import UserPermissions from './console/users/UserPermissions.vue';
import SubscriptionsIndex from './console/subscriptions/SubscriptionsIndex.vue';
import SubscriptionPlansIndex from './console/subscriptions/SubscriptionPlansIndex.vue';
import SubscriptionLink from './console/subscriptions/SubscriptionLink.vue';
import BrandingIndex from './console/branding/BrandingIndex.vue';
import SettingsIndex from './console/tenant-settings/SettingsIndex.vue';
import ConnectionsIndex from './console/tenant-connections/ConnectionsIndex.vue';
import UsageIndex from './console/usage/UsageIndex.vue';
import StorageIndex from './console/storage/StorageIndex.vue';
import LabelsIndex from './console/labels/LabelsIndex.vue';
import ApiClientsIndex from './console/api-clients/ApiClientsIndex.vue';
import WebhookIntegrationsIndex from './console/webhook-integrations/WebhookIntegrationsIndex.vue';
import WebhookIntegrationDetails from './console/webhook-integrations/WebhookIntegrationDetails.vue';
import FTPUsersIndex from './console/ftp-users/FTPUsersIndex.vue';
import AccesslinkIndex from './accesslink2/AccesslinkIndex.vue';
import AccesslinkLogin from './accesslink2/AccesslinkLogin.vue';
import AccesslinkListView from './accesslink2/AccesslinkListView.vue';
import AccesslinkNodeView from './accesslink2/AccesslinkNodeView.vue';
import AccesslinkPodView from './accesslink2/AccesslinkPodView.vue';
import UploadlinkIndex from './uploadlink2/UploadlinkIndex.vue';
import UploadlinkLogin from './uploadlink2/UploadlinkLogin.vue';
import UploadlinkView from './uploadlink2/UploadlinkView.vue';
import EmbeddedUploadIndex from './embedded/EmbeddedUploadIndex.vue';
import EmbeddedIndex from './embedded/EmbeddedIndex.vue';
import GuidedRecordingPublic from '@/routes/guided-recording/GuidedRecordingPublic.vue';
import ExpiredPage from './default/ExpiredPage.vue';
import ForbiddenPage from './default/ForbiddenPage.vue';
import NotFoundPage from './default/NotFoundPage.vue';
import UnsupportedBrowserPage from './default/UnsupportedBrowserPage.vue';
import UnsupportedPremiereVersionPage from './default/UnsupportedPremiereVersionPage.vue';
import { useLoginRedirect } from '@/components/login-redirect.js';
import MobileAccountLoginProvider from '@/routes/account/mobile/MobileAccountLoginProvider.vue';
import AccountDeleteRequest from './account/AccountDeleteRequest.vue';
import Helper from '@/components/helper.js';

const MAIN_LAYOUT = 'MainLayout';
const LOGIN_LAYOUT = 'LoginLayout';
const LOGIN_LAYOUT_WIDE = 'LoginLayoutWide';
const CONSOLE_LAYOUT = 'ConsoleLayout';
const LOADING_LAYOUT = 'LoadingLayout';
const BLANK_LAYOUT = 'BlankLayout';
const ACCOUNT_LOGOUT_ROUTE = '/account/logout';
const ACCOUNT_FORCED_LOGOUT_ROUTE = '/account/forced-logout';
const UNSUPPORTED_BROWSER_ROUTE = '/unsupported-browser';
const UNSUPPORTED_PREMIERE_PRO_ROUTE = '/unsupported-premiere-pro';

export function createAppRouter() {
  const routes = [
    { path: '/soon', component: SoonPage, meta: { layout: LOGIN_LAYOUT, requiresAuth: false} },
    { path: '/account/login', component: AccountLogin, meta: { layout: LOGIN_LAYOUT, requiresAuth: false} },
    { path: '/account/login/:provider', component: AccountLoginProvider, meta: { layout: LOGIN_LAYOUT, requiresAuth: false } },
    { path: '/m/account/login/:provider', component: MobileAccountLoginProvider, meta: { layout: LOGIN_LAYOUT, requiresAuth: false } },
    { path: '/account/otp', component: AccountLoginOTP, meta: { layout: LOGIN_LAYOUT, requiresAuth: false} },
    { path: '/account/enable-mfa', component: AccountEnableMFA, meta: { layout: LOGIN_LAYOUT, requiresAuth: true} },
    { path: '/account/external', component: AccountLoginExternal, meta: { layout: LOGIN_LAYOUT, requiresAuth: false} },
    { path: '/account/policy', component: PrivacyPolicy, meta: { layout: LOGIN_LAYOUT_WIDE, requiresAuth: false} },

    { path: '/account/forgot-password', component: ForgotPassword, meta: { layout: LOGIN_LAYOUT, requiresAuth: false} },
    { path: '/account/reset-password/:email?/:code?', component: ResetPassword, meta: { layout: LOGIN_LAYOUT, requiresAuth: false} },
    { path: '/account/reset-temporary-password', component: ResetTemporaryPassword, meta: { layout: LOGIN_LAYOUT, requiresAuth: false} },
    { path: '/account/verify/:email?', component: AccountVerify, meta: { layout: LOGIN_LAYOUT, requiresAuth: false} },
    { path: '/account/complete-password', component: CompletePassword, meta: { layout: LOGIN_LAYOUT, requiresAuth: false} },

    { path: '/account/bandwidth-exceeded', component: BandwidthExceeded, meta: { layout: LOGIN_LAYOUT, requiresAuth: false} },
    { path: '/account/tenant-expired', component: TenantExpired, meta: { layout: LOGIN_LAYOUT, requiresAuth: true} },
    { path: UNSUPPORTED_BROWSER_ROUTE, component: UnsupportedBrowserPage, meta: { layout: LOGIN_LAYOUT, requiresAuth: false } },
    { path: UNSUPPORTED_PREMIERE_PRO_ROUTE, component: UnsupportedPremiereVersionPage, meta: { layout: LOGIN_LAYOUT, requiresAuth: false } },
    { path: '/account/select-subscription', component: SelectSubscription, meta: { layout: LOGIN_LAYOUT, requiresAuth: false} },

    { path: '/account/logout', component: AccountLogout, meta: { layout: LOGIN_LAYOUT, requiresAuth: true} },
    { path: '/account/forced-logout', component: ForcedLogout, meta: { layout: LOGIN_LAYOUT, requiresAuth: false} },

    // US #12434 - Temporary disable account creation
    // { path: '/account-create', component: AccountSignUp, meta: { layout: LOGIN_LAYOUT, requiresAuth: false} },
    { path: '/account-create-success', component: AccountSignedUp, meta: { layout: LOGIN_LAYOUT, requiresAuth: false} },
    { path: '/account/salesman', component: AccountSalesman, meta: { layout: LOGIN_LAYOUT, requiresAuth: false} },
    { path: '/account/request-delete', component: AccountDeleteRequest, meta: { layout: LOGIN_LAYOUT, requiresAuth: false} },

    { path: '/tenant/email', component: ValidateTenantEmail, meta: { layout: LOGIN_LAYOUT, requiresAuth: false}},

    { path: '/', component: Index, meta: { layout: MAIN_LAYOUT, requiresAuth: true } },
    { name: 'node:view', path: '/node/:id', component: NodesIndex, meta: { layout: MAIN_LAYOUT, requiresAuth: true } },
    { name: 'pod:view', path: '/pod/:id', component: PodIndex, meta: { layout: MAIN_LAYOUT, requiresAuth: true } },
    { path: '/search', component: SearchIndex, meta: { layout: MAIN_LAYOUT, requiresAuth: true } },
    { path: '/recycle-bin/:id', name: 'recycle-bin', component: RecycleBinIndex, meta: { layout: MAIN_LAYOUT, requiresAuth: true} },

    { path: '/console/index', component: AccountIndex, meta: {layout: CONSOLE_LAYOUT, requiresAuth: true}},
    { path: '/console/profile/mfa', component: ProfileMFA, meta: {layout: CONSOLE_LAYOUT, requiresAuth: true}},
    { path: '/console/profile/notifications', component: ProfileNotifications, meta: {layout: CONSOLE_LAYOUT, requiresAuth: true}},

    { path: '/console/users', component: UsersIndex, meta: {layout: CONSOLE_LAYOUT, requiresAuth: true}},
    { path: '/console/users/permissions', component: UserPermissions, meta: {layout: CONSOLE_LAYOUT, requiresAuth: true}},
    { path: '/console/subscriptions', component: SubscriptionsIndex, meta: {layout: CONSOLE_LAYOUT, requiresAuth: true}},
    // Deprecated subscription UI: retain direct access, but expose no automatic navigation to it.
    { path: '/console/subscription-plans', component: SubscriptionPlansIndex, meta: {layout: CONSOLE_LAYOUT, requiresAuth: true}},
    { path: '/console/subscription-link', component: SubscriptionLink, meta: {layout: CONSOLE_LAYOUT, requiresAuth: true}},
    { path: '/console/branding', component: BrandingIndex, meta: {layout: CONSOLE_LAYOUT, requiresAuth: true}},
    { path: '/console/tenant-settings', component: SettingsIndex, meta: {layout: CONSOLE_LAYOUT, requiresAuth: true}},
    { path: '/console/tenant-connections', component: ConnectionsIndex, meta: {layout: CONSOLE_LAYOUT, requiresAuth: true}},
    { path: '/console/usage', component: UsageIndex, meta: {layout: CONSOLE_LAYOUT, requiresAuth: true}},
    { path: '/console/storage', component: StorageIndex, meta: {layout: CONSOLE_LAYOUT, requiresAuth: true}},
    { path: '/console/labels', component: LabelsIndex, meta: {layout: CONSOLE_LAYOUT, requiresAuth: true}},
    { path: '/console/api-clients', component: ApiClientsIndex, meta: {layout: CONSOLE_LAYOUT, requiresAuth: true}},
    { path: '/console/webhook-integrations', component: WebhookIntegrationsIndex, meta: {layout: CONSOLE_LAYOUT, requiresAuth: true}},
    { path: '/console/webhook-integrations/:integrationId', component: WebhookIntegrationDetails, meta: {layout: CONSOLE_LAYOUT, requiresAuth: true}},
    { path: '/console/ftp-users', component: FTPUsersIndex, meta: {layout: CONSOLE_LAYOUT, requiresAuth: true}},

    // external view of a pod
    { name: 'accesslink:index', path: '/reviewaccess/:code', component: AccesslinkIndex, meta: { layout: LOADING_LAYOUT, requiresAuth: false }},
    { name: 'accesslink:login', path: '/reviewaccess/:code/login', component: AccesslinkLogin, meta: { layout: LOGIN_LAYOUT, requiresAuth: false }},
    { name: 'accesslink:view', path: '/reviewaccess/:code/view', component: AccesslinkListView, meta: { layout: BLANK_LAYOUT, requiresAuth: false }},
    { name: 'accesslink:view:node', path: '/reviewaccess/:code/view/node/:nodeid', component: AccesslinkNodeView, meta: { layout: BLANK_LAYOUT, requiresAuth: false }},
    { name: 'accesslink:view:pod', path: '/reviewaccess/:code/view/:nodeid?/:podid', component: AccesslinkPodView, meta: { layout: BLANK_LAYOUT, requiresAuth: false }},

    // external view to upload files
    { path: '/uploadaccess/:code', component: UploadlinkIndex, meta: { layout: LOADING_LAYOUT, requiresAuth: false}},
    { path: '/uploadaccess/:code/login', component: UploadlinkLogin, meta: { layout: LOGIN_LAYOUT, requiresAuth: false}},
    { path: '/uploadaccess/:code/view', component: UploadlinkView, meta: { layout: BLANK_LAYOUT, requiresAuth: false}},

    // external view of an embedded pod, the layout will not be updated
    { path: '/embedded-upload/:code', component: EmbeddedUploadIndex, meta: { layout: BLANK_LAYOUT, requiresAuth: false}},
    { path: '/embedded/:code', component: EmbeddedIndex, meta: { layout: BLANK_LAYOUT, requiresAuth: false}},
    { name: 'guided-recording:public', path: '/guided-recording/:code', component: GuidedRecordingPublic, meta: { layout: BLANK_LAYOUT, requiresAuth: false }},

    // status 401 code 1012 your account has expired
    { path: '/expired', component: ExpiredPage, meta: { layout: LOGIN_LAYOUT, requiresAuth: true} },

    // status 403 response forbidden component
    { path: '/forbidden', component: ForbiddenPage, meta: { layout: LOGIN_LAYOUT, requiresAuth: false} },

    // status 404 page not found component
    { path: '/:patchMatch(.*)*', component: NotFoundPage, meta: { layout: LOGIN_LAYOUT, requiresAuth: false} },
  ];

  const router = createRouter({
    history: createWebHistory(),
    routes
  });

  router.beforeResolve(async (to) => {
    const authStore = useAuthStore();

    if (authStore.sessionInvalidatedDialogVisible || authStore.sessionInvalidatedLogoutPending) {
      return true;
    }

    if (Helper.isUnsupportedBrowser()) {
      const allowedRoutes = [
        ACCOUNT_LOGOUT_ROUTE,
        ACCOUNT_FORCED_LOGOUT_ROUTE,
        UNSUPPORTED_BROWSER_ROUTE,
      ];

      if (!allowedRoutes.includes(to.path)) {
        return { path: UNSUPPORTED_BROWSER_ROUTE };
      }
    } else if (to.path === UNSUPPORTED_BROWSER_ROUTE) {
      return { path: '/' };
    }

    if (Helper.isUnsupportedPremiereProVersion()) {
      const allowedRoutes = [
        ACCOUNT_LOGOUT_ROUTE,
        ACCOUNT_FORCED_LOGOUT_ROUTE,
        UNSUPPORTED_PREMIERE_PRO_ROUTE,
      ];

      if (!allowedRoutes.includes(to.path)) {
        return { path: UNSUPPORTED_PREMIERE_PRO_ROUTE };
      }
    } else if (to.path === UNSUPPORTED_PREMIERE_PRO_ROUTE) {
      return { path: '/' };
    }

    if (to.path === '/account/bandwidth-exceeded' && !authStore.user) {
      return '/';
    }

    // for not authenticated pages
    if (!to.meta.requiresAuth) {
      return true;
    }

    // when the tenant subscription is expired - block the user
    if (authStore.tenantIsExpired) {
      const allowedUrls = [
        '/account/logout',
        '/account/tenant-expired',
        '/console/index',
        '/console/usage',
        '/console/subscription-plans',
      ];

      if (!allowedUrls.includes(to.path)) {
        return { path: '/account/tenant-expired' };
      }
    }

    try {
      await authStore.refreshCognitoUser();

      if (to.path.startsWith('/usage')) {
        return { path: '/' };
      }

      return true;
    } catch (ew) {
      if (ew && ew.name !== 'UserUnAuthenticatedException') {
        console.error(ew);
      }

      const { setLocation } = useLoginRedirect();
      setLocation(to.fullPath);

      if (to.path === '/') {
        return { path: '/account/login' };
      }

      return { path: '/account/login?error=sessionExpired' };
    }
  });

  router.beforeEach(() => {
    if (!history || !history.state || !history.state.key) {
      window.localStorage.browsingHistory = '{}';
    }

    return true;
  })


  router.afterEach((to, from, failure) => {
    if (failure) {
      return;
    }

    // Get the key from the history API to distinguish between the differen URL addresses.
    const key = history?.state?.key;

    if (!key) {
      return;
    }

    let browsingHistory = JSON.parse(window.localStorage.browsingHistory || '{}');
    if (browsingHistory[key]) {
      // Back button is pressed.
      // Get the corresponding root node id from the history and apply it.
      window.localStorage.contextnodeid = browsingHistory[key];

      const authStore = useAuthStore();
      authStore._setRootNodeId(window.localStorage.contextnodeid);

    } else {
      // Save the current root node id in the local storage
      // so that we know which rootnodeId is related to the current browser history.
      browsingHistory[key] =  window.localStorage.contextnodeid;
      window.localStorage.browsingHistory = JSON.stringify(browsingHistory);
    }
  });

  return router;
}
